import React, { useState } from 'react';
import {
  Tabs,
  Tab,
  Box,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { PageTitle, ListLayoutPanel } from '../../../../components/common';
import { ReporteContableService } from '../services/reporteContableService';
import { EjercicioService } from '../../ejercicios/services/ejercicioService';
import type { BalanceGeneral, EstadoResultados, BalanceComprobacionLinea } from '../types/reportes';
import type { EjercicioContable } from '../../ejercicios/types/ejercicio';
import { PERIODO_LABELS } from '../../ejercicios/types/ejercicio';
import { useEffect } from 'react';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const ReportesContablesPage: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [ejercicios, setEjercicios] = useState<EjercicioContable[]>([]);
  const [selectedEjercicio, setSelectedEjercicio] = useState<number>(0);
  const [periodoDesde, setPeriodoDesde] = useState<number>(1);
  const [periodoHasta, setPeriodoHasta] = useState<number>(12);
  const [moneda, setMoneda] = useState<string>('MN');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [balanceGeneral, setBalanceGeneral] = useState<BalanceGeneral | null>(null);
  const [estadoResultados, setEstadoResultados] = useState<EstadoResultados | null>(null);
  const [balanceComprobacion, setBalanceComprobacion] = useState<BalanceComprobacionLinea[]>([]);

  useEffect(() => {
    loadEjercicios();
  }, []);

  const loadEjercicios = async () => {
    try {
      const data = await EjercicioService.getAll();
      setEjercicios(data);
      if (data.length > 0) setSelectedEjercicio(data[0].idEjercicio);
    } catch (err) {
      // handled
    }
  };

  const handleGenerarReporte = async () => {
    setLoading(true);
    setError(null);
    try {
      if (tabIndex === 0) {
        const data = await ReporteContableService.getBalanceGeneral(selectedEjercicio, periodoHasta, moneda);
        setBalanceGeneral(data);
      } else if (tabIndex === 1) {
        const data = await ReporteContableService.getEstadoResultados(selectedEjercicio, periodoDesde, periodoHasta, moneda);
        setEstadoResultados(data);
      } else {
        const data = await ReporteContableService.getBalanceComprobacion(selectedEjercicio, periodoDesde, periodoHasta);
        setBalanceComprobacion(data);
      }
    } catch (err) {
      setError('Error al generar el reporte');
    } finally {
      setLoading(false);
    }
  };

  const periodoOptions = Array.from({ length: 15 }, (_, i) => i);

  return (
    <ListLayoutPanel>
      <PageTitle
        title="Reportes Contables"
        subtitle="Balance General, Estado de Resultados, Balance de Comprobación"
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}>
          <Tab label="Balance General" />
          <Tab label="Estado de Resultados" />
          <Tab label="Balance de Comprobación" />
        </Tabs>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Ejercicio</InputLabel>
              <Select
                value={selectedEjercicio}
                label="Ejercicio"
                onChange={(e) => setSelectedEjercicio(Number(e.target.value))}
              >
                {ejercicios.map(ej => (
                  <MenuItem key={ej.idEjercicio} value={ej.idEjercicio}>{ej.anio}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {tabIndex !== 0 && (
            <Grid size={{ xs: 12, sm: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Desde</InputLabel>
                <Select value={periodoDesde} label="Desde" onChange={(e) => setPeriodoDesde(Number(e.target.value))}>
                  {periodoOptions.map(p => (
                    <MenuItem key={p} value={p}>{String(p).padStart(2, '0')} - {PERIODO_LABELS[p]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Hasta</InputLabel>
              <Select value={periodoHasta} label="Hasta" onChange={(e) => setPeriodoHasta(Number(e.target.value))}>
                {periodoOptions.map(p => (
                  <MenuItem key={p} value={p}>{String(p).padStart(2, '0')} - {PERIODO_LABELS[p]}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {tabIndex !== 2 && (
            <Grid size={{ xs: 12, sm: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Moneda</InputLabel>
                <Select value={moneda} label="Moneda" onChange={(e) => setMoneda(e.target.value)}>
                  <MenuItem value="MN">MN (S/)</MenuItem>
                  <MenuItem value="ME">ME (US$)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 2 }}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleGenerarReporte}
              disabled={loading}
              fullWidth
            >
              Generar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <CircularProgress />}

      {/* Balance General */}
      <TabPanel value={tabIndex} index={0}>
        {balanceGeneral && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" align="center" gutterBottom>
              Balance General al Periodo {PERIODO_LABELS[balanceGeneral.periodoHasta]} - {balanceGeneral.anio}
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>ACTIVO</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      {balanceGeneral.activo.map((linea, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ pl: linea.nivel * 2 }}>{linea.codigoCuenta}</TableCell>
                          <TableCell>{linea.descripcion}</TableCell>
                          <TableCell align="right">{linea.saldo.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={2}><strong>TOTAL ACTIVO</strong></TableCell>
                        <TableCell align="right"><strong>{balanceGeneral.totalActivo.toFixed(2)}</strong></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>PASIVO</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      {balanceGeneral.pasivo.map((linea, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ pl: linea.nivel * 2 }}>{linea.codigoCuenta}</TableCell>
                          <TableCell>{linea.descripcion}</TableCell>
                          <TableCell align="right">{linea.saldo.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={2}><strong>TOTAL PASIVO</strong></TableCell>
                        <TableCell align="right"><strong>{balanceGeneral.totalPasivo.toFixed(2)}</strong></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>PATRIMONIO</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      {balanceGeneral.patrimonio.map((linea, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ pl: linea.nivel * 2 }}>{linea.codigoCuenta}</TableCell>
                          <TableCell>{linea.descripcion}</TableCell>
                          <TableCell align="right">{linea.saldo.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={2}><strong>TOTAL PATRIMONIO</strong></TableCell>
                        <TableCell align="right"><strong>{balanceGeneral.totalPatrimonio.toFixed(2)}</strong></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={2}><strong>TOTAL PASIVO + PATRIMONIO</strong></TableCell>
                        <TableCell align="right"><strong>{(balanceGeneral.totalPasivo + balanceGeneral.totalPatrimonio).toFixed(2)}</strong></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Paper>
        )}
      </TabPanel>

      {/* Estado de Resultados */}
      <TabPanel value={tabIndex} index={1}>
        {estadoResultados && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" align="center" gutterBottom>
              Estado de Resultados {PERIODO_LABELS[estadoResultados.periodoDesde]} a {PERIODO_LABELS[estadoResultados.periodoHasta]} - {estadoResultados.anio}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="right">Monto</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow><TableCell colSpan={3}><strong>INGRESOS</strong></TableCell></TableRow>
                  {estadoResultados.ingresos.map((l, i) => (
                    <TableRow key={`i-${i}`}>
                      <TableCell sx={{ pl: l.nivel * 2 }}>{l.codigoCuenta}</TableCell>
                      <TableCell>{l.descripcion}</TableCell>
                      <TableCell align="right">{l.saldo.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={2}><strong>TOTAL INGRESOS</strong></TableCell>
                    <TableCell align="right"><strong>{estadoResultados.totalIngresos.toFixed(2)}</strong></TableCell>
                  </TableRow>
                  <TableRow><TableCell colSpan={3}><strong>GASTOS</strong></TableCell></TableRow>
                  {estadoResultados.gastos.map((l, i) => (
                    <TableRow key={`g-${i}`}>
                      <TableCell sx={{ pl: l.nivel * 2 }}>{l.codigoCuenta}</TableCell>
                      <TableCell>{l.descripcion}</TableCell>
                      <TableCell align="right">{l.saldo.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={2}><strong>TOTAL GASTOS</strong></TableCell>
                    <TableCell align="right"><strong>{estadoResultados.totalGastos.toFixed(2)}</strong></TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: estadoResultados.resultadoNeto >= 0 ? 'success.light' : 'error.light' }}>
                    <TableCell colSpan={2}><strong>RESULTADO NETO</strong></TableCell>
                    <TableCell align="right"><strong>{estadoResultados.resultadoNeto.toFixed(2)}</strong></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </TabPanel>

      {/* Balance de Comprobación */}
      <TabPanel value={tabIndex} index={2}>
        {balanceComprobacion.length > 0 && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" align="center" gutterBottom>
              Balance de Comprobación
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="right">Sumas Debe</TableCell>
                    <TableCell align="right">Sumas Haber</TableCell>
                    <TableCell align="right">Saldo Deudor</TableCell>
                    <TableCell align="right">Saldo Acreedor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {balanceComprobacion.map((linea, i) => (
                    <TableRow key={i} sx={{ fontWeight: linea.nivel === 1 ? 'bold' : 'normal' }}>
                      <TableCell sx={{ pl: linea.nivel * 2 }}>
                        <Typography fontWeight={linea.nivel <= 2 ? 'bold' : 'normal'} variant="body2">
                          {linea.codigoCuenta}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={linea.nivel <= 2 ? 'bold' : 'normal'} variant="body2">
                          {linea.descripcion}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{linea.sumasDebe.toFixed(2)}</TableCell>
                      <TableCell align="right">{linea.sumasHaber.toFixed(2)}</TableCell>
                      <TableCell align="right">{linea.saldoDeudor.toFixed(2)}</TableCell>
                      <TableCell align="right">{linea.saldoAcreedor.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </TabPanel>
    </ListLayoutPanel>
  );
};

export default ReportesContablesPage;
